import { libelles } from './i18n.js';
/**
 * Formats d'affichage, écrits à la main plutôt que délégués à `Intl` : le
 * moteur doit rendre exactement le même HTML sur une machine de développement,
 * dans la CI et sur le serveur de publication, quelle que soit l'ICU du Node
 * installé. Les instantanés ne bougent pas.
 */
const ESPACE_FINE = ' ';
const ESPACE_INSECABLE = ' ';
const SYMBOLES = {
    EUR: '€',
    CHF: 'CHF',
    GBP: '£',
    USD: '$',
};
function eclate(date) {
    const [a, m, j] = date.split('-');
    if (a === undefined || m === undefined || j === undefined) {
        throw new Error(`date invalide : ${date}`);
    }
    return { annee: Number(a), mois: Number(m), jour: Number(j) };
}
function nomMois(langue, mois, court) {
    const table = court ? libelles(langue).moisCourts : libelles(langue).mois;
    const nom = table[mois - 1];
    if (nom === undefined)
        throw new Error(`mois invalide : ${String(mois)}`);
    return nom;
}
/** « 5 septembre 2026 » / « 5 September 2026 ». */
export function formaterDate(date, langue, court = false) {
    const { annee, mois, jour } = eclate(date);
    return `${String(jour)} ${nomMois(langue, mois, court)} ${String(annee)}`;
}
/**
 * « 5 septembre — 25 octobre 2026 » : l'année n'est répétée que si elle
 * change, le mois n'est répété que s'il change.
 */
export function formaterPeriode(debut, fin, langue, court = false) {
    const d = eclate(debut);
    const f = eclate(fin);
    const tiret = ` — `;
    if (d.annee !== f.annee) {
        return `${formaterDate(debut, langue, court)}${tiret}${formaterDate(fin, langue, court)}`;
    }
    if (d.mois !== f.mois) {
        return `${String(d.jour)} ${nomMois(langue, d.mois, court)}${tiret}${formaterDate(fin, langue, court)}`;
    }
    return `${String(d.jour)}${tiret}${formaterDate(fin, langue, court)}`;
}
/** « 18 h — 21 h » en français, « 6 pm — 9 pm » en anglais. */
export function formaterHeure(heure, langue) {
    const [h, m] = heure.split(':');
    if (h === undefined || m === undefined)
        throw new Error(`heure invalide : ${heure}`);
    const heures = Number(h);
    const minutes = Number(m);
    if (langue === 'fr') {
        return minutes === 0
            ? `${String(heures)}${ESPACE_INSECABLE}h`
            : `${String(heures)}${ESPACE_INSECABLE}h${ESPACE_INSECABLE}${m}`;
    }
    const suffixe = heures < 12 ? 'am' : 'pm';
    const douze = heures % 12 === 0 ? 12 : heures % 12;
    return minutes === 0
        ? `${String(douze)}${ESPACE_INSECABLE}${suffixe}`
        : `${String(douze)}:${m}${ESPACE_INSECABLE}${suffixe}`;
}
function groupes(entier, separateur) {
    let sortie = '';
    for (let i = 0; i < entier.length; i += 1) {
        const reste = entier.length - i;
        sortie += entier[i] ?? '';
        if (reste > 1 && reste % 3 === 1)
            sortie += separateur;
    }
    return sortie;
}
/** « 18 000 € » en français, « €18,000 » en anglais. HT, comme en base. */
export function formaterPrix(prix, langue) {
    const centimes = Math.round(prix.montantCentimes);
    const euros = Math.trunc(centimes / 100);
    const reste = centimes % 100;
    const symbole = SYMBOLES[prix.devise];
    if (langue === 'fr') {
        const entier = groupes(String(euros), ESPACE_FINE);
        const decimal = reste === 0 ? '' : `,${String(reste).padStart(2, '0')}`;
        return `${entier}${decimal}${ESPACE_INSECABLE}${symbole}`;
    }
    const entier = groupes(String(euros), ',');
    const decimal = reste === 0 ? '' : `.${String(reste).padStart(2, '0')}`;
    return `${symbole}${entier}${decimal}`;
}
/** Montant nu pour le JSON-LD et les attributs machine : « 18000.00 ». */
export function montantBrut(prix) {
    return (prix.montantCentimes / 100).toFixed(2);
}
/** « Édition 2/5 + 2 EA », « Épreuve d'artiste, édition de 5 ». */
export function formaterEdition(edition, langue) {
    const l = libelles(langue);
    const ea = edition.epreuvesArtiste !== undefined && edition.epreuvesArtiste > 0
        ? ` + ${l.epreuvesArtiste(edition.epreuvesArtiste)}`
        : '';
    if (edition.epreuveArtiste === true)
        return `${l.epreuveArtiste(edition.tirage)}${ea}`;
    if (edition.exemplaire !== undefined) {
        return `${l.edition(edition.exemplaire, edition.tirage)}${ea}`;
    }
    return `${l.editionDe(edition.tirage)}${ea}`;
}
/** Jour de la semaine ISO (1 = lundi) vers son libellé. */
export function nomJour(langue, index) {
    const nom = libelles(langue).jours[index];
    if (nom === undefined)
        throw new Error(`jour invalide : ${String(index)}`);
    return nom;
}
/** Date RFC 822, pour le flux RSS. Minuit, heure de Paris en hiver. */
export function dateRfc822(date) {
    return new Date(`${date}T00:00:00Z`).toUTCString();
}
/** Les événements suivent l’heure de Paris, comme les pages d’inscription. */
export function formaterDateEvenement(date, langue, journee) {
    return new Intl.DateTimeFormat(langue === 'fr' ? 'fr-FR' : 'en-GB', {
        dateStyle: 'long',
        ...(journee ? {} : { timeStyle: 'short' }),
        timeZone: 'Europe/Paris',
    }).format(new Date(date));
}
//# sourceMappingURL=formats.js.map